import { LuEllipsis, LuKanban, LuList, LuPlus } from 'react-icons/lu';

import { GlobalAccount, TaskView } from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PageTitleRow } from '@/components/ui/page-title-row';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select';
import { useTenantScopeLabel } from 'views/auth/tenant-selection';
import { SearchInput } from 'widgets';

import { TaskLayout, useTaskViewState } from '../task-state';
import { TaskMultiFilter } from './TaskMultiFilter';
import { TaskSortDropdownMenu } from './TaskSortDropdownMenu';

interface TaskFilterBarProps {
    taskCount: number;
    rawTasks: TaskView[];
    householdMap: Map<string, GlobalAccount>;
    onSearchChange: (value: string) => void;
    onTemplateClick: () => void;
    onNewTask: () => void;
    onExportCsv: () => void;
}

export const TaskFilterBar = ({
    taskCount,
    rawTasks,
    householdMap,
    onSearchChange,
    onTemplateClick,
    onNewTask,
    onExportCsv,
}: TaskFilterBarProps) => {
    const { taskViewState, setTaskViewState } = useTaskViewState();
    const tenantLabel = useTenantScopeLabel();
    const subtitle = tenantLabel ? `${taskCount} tasks · ${tenantLabel}` : `${taskCount} tasks`;

    return (
        <PageTitleRow
            actions={
                <div className="flex items-center gap-2">
                    <SearchInput placeholder="Search tasks..." onChange={(e) => onSearchChange(e.target.value)} round />

                    <TaskMultiFilter tasks={rawTasks} householdMap={householdMap} />
                    <TaskSortDropdownMenu />

                    <Button variant="outline2" size="sm" onClick={onNewTask}>
                        <LuPlus className="h-4 w-4" />
                        Task
                    </Button>

                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline2" size="sm" className="px-2">
                                <LuEllipsis className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={onTemplateClick}>Templates</DropdownMenuItem>
                            {taskViewState.layout === TaskLayout.LIST && (
                                <DropdownMenuItem onClick={onExportCsv}>Export .csv</DropdownMenuItem>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            }
            className="gap-4"
        >
            <div className="flex shrink-0 flex-col">
                <Select
                    value={taskViewState.layout}
                    onValueChange={(val) => setTaskViewState({ layout: val as TaskLayout })}
                >
                    <SelectTrigger className="text-cocoa-1000 hover:text-cocoa-900 font-heading h-auto w-auto shrink-0 cursor-pointer gap-1 border-0 bg-transparent p-0 text-3xl font-medium whitespace-nowrap shadow-none focus:ring-0 focus:ring-offset-0 [&>svg]:mt-0.5 [&>svg]:opacity-100">
                        {taskViewState.layout === TaskLayout.LIST ? 'Tasks List' : 'Tasks Kanban'}
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={TaskLayout.LIST}>
                            <span className="flex items-center gap-2">
                                <LuList className="h-4 w-4" />
                                Tasks List
                            </span>
                        </SelectItem>
                        <SelectItem value={TaskLayout.KANBAN}>
                            <span className="flex items-center gap-2">
                                <LuKanban className="h-4 w-4" />
                                Tasks Kanban
                            </span>
                        </SelectItem>
                    </SelectContent>
                </Select>
                <p className="text-cocoa-600 mt-0.5 text-sm font-thin">{subtitle}</p>
            </div>
        </PageTitleRow>
    );
};
